import type {
  AccountNotFound,
  AdminRemovalRefused,
  ApiContext,
  ApiNotAuthorized,
  InvalidFormField,
  StoreAdmin,
  StoreNotFound,
  StoreRole,
  StoreRoleForbidden,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { BackendApi } from "./api";

interface StoreAdminCall {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  storeId: string;
}

export function loadStoreAdmins(
  o: StoreAdminCall,
): AsyncResult<StoreAdmin[], ApiNotAuthorized | StoreNotFound | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.stores.admins(ctx, o.storeId));
}

/** The account has to exist already; a Manager may add Staff only. Answers with the whole list. */
export function addStoreAdmin(
  o: StoreAdminCall & { email: string; role: StoreRole },
): AsyncResult<
  StoreAdmin[],
  ApiNotAuthorized | StoreNotFound | AccountNotFound | StoreRoleForbidden | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.stores.addAdmin(ctx, o.storeId, o.email, o.role));
}

/** Not yourself, not the last Owner; a Manager may remove Staff only. Answers with the whole list. */
export function removeStoreAdmin(
  o: StoreAdminCall & { userId: string },
): AsyncResult<StoreAdmin[], ApiNotAuthorized | StoreNotFound | AdminRemovalRefused | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.stores.removeAdmin(ctx, o.storeId, o.userId));
}

/** Owner only, not on yourself; demoting the last Owner is refused. Answers with the whole list. */
export function changeStoreAdminRole(
  o: StoreAdminCall & { userId: string; role: StoreRole },
): AsyncResult<
  StoreAdmin[],
  ApiNotAuthorized | StoreNotFound | AdminRemovalRefused | StoreRoleForbidden | InvalidFormField
> {
  return o.authHandler.callApi((ctx) => o.client.stores.changeAdminRole(ctx, o.storeId, o.userId, o.role));
}
